import './style.scss';
import { ApplySymbolInput } from '../hooks';
import { OverseasRealEstateFullInfo } from 'views/pages/symbol/module/fullInfo';
import { OverseasRealEstateThumbnail } from 'views/pages/symbol/module/thumbnail';
import { OverseasRealEstateBasicInfo } from 'views/pages/symbol/module/basicInfo';
import { Description, DescriptionAndLink, labelColor, labelSize } from 'views/mutual/description';
import { Caption } from 'views/mutual/caption';
import { CheckLabel } from 'views/mutual/checkLabel';
import { Button, ButtonFill, ButtonSize } from 'views/mutual/button';
import { OverseasRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';
import { FormikProps } from 'formik';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { icons } from 'constants/icons';
import { IconColor, IconSize, SvgIcon } from 'views/mutual/svgIcon';
import { useMessageModal } from 'views/mutual/modal/messageModal/hooks';
import { UnitSelectorPosition } from 'views/templates/module/unitSelector';
import { RequestInput } from 'views/pages/symbol/module/requestInput/amountInput';
import { PagePath, PagePathFactory } from 'constants/pagePath';
import { ProductType } from 'constants/productType';
import { formatPrice } from 'views/utils/converter';
import { MessageModal } from 'views/mutual/modal/messageModal';

type OverseasRealEstateSymbolDetailProps = {
  symbolDetail: OverseasRealEstateSymbolDetailInfo;
  formik: FormikProps<ApplySymbolInput>;
  interest: number | null;
  amountRaw: number | null;
  totalAmountRaw: number | null;
  canApplySymbol: boolean;
  transferDateProps: any;
  checkContractDocumentProps: any;
  amountProps: any;
};

export const OverseasRealEstateSymbolDetail = ({
  symbolDetail,
  formik,
  interest,
  totalAmountRaw,
  canApplySymbol,
  amountProps,
}: OverseasRealEstateSymbolDetailProps) => {
  useCustomLocationName(symbolDetail.SymbolName);
  const { modalProps, showMessageModal } = useMessageModal();
  return (
    <div className="overseasRealEstate">
      {!symbolDetail.Tradable && (
        <div className="disableApplyDescription">
          <Description
            label="こちらの案件は現在お申し込みいただけません"
            labelColor={labelColor.red}
            labelSize={labelSize.large}
          />
        </div>
      )}
      <div className="programDetail">
        <div className="programDetail_card">
          <OverseasRealEstateThumbnail {...symbolDetail} />
          <OverseasRealEstateBasicInfo isFull={true} {...symbolDetail} />
          <OverseasRealEstateFullInfo {...symbolDetail!} />
          <div className="programDetail_card_wrapper">
            <a
              className="documentLink"
              href={symbolDetail.PreContractDocumentURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                formik.setFieldValue('isContractDocumentOpened', true);
              }}
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">契約締結前交付書面</span>
                </div>
              </Button>
            </a>
            <a
              className="documentLink"
              href={symbolDetail.SimplePreContractDocumentURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">簡易版</span>
                </div>
              </Button>
            </a>
            <a className="documentLink" href={symbolDetail.TotalDocumentURL} target="_blank" rel="noopener noreferrer">
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">総合版</span>
                </div>
              </Button>
            </a>
          </div>
          <div className="description">
            <Description
              labelColor={labelColor.gray}
              labelSize={labelSize.small}
              label="（※1）但し事業が終了した場合は事業終了日となります。"
            />
            <Description
              labelColor={labelColor.gray}
              labelSize={labelSize.small}
              label="（※2）会社法上の法人（株式会社、合同会社等）および特例有限会社以外の法人のお客様は、念書、定款等の提出をお願いする場合がございます。"
            />
          </div>
        </div>
        <div className="programDetail_request">
          <RequestInput
            overseasRealEstateProps={{
              interest: interest,
              accruedInterest: symbolDetail.AccruedInterest,
              totalAmountRaw: totalAmountRaw,
              labelValueUnitSelectionProps: { ...amountProps.unitSelectorProps },
              dateInputProps: {
                date: formik.values.transferDate,
                name: 'transferDate',
                // 過去日かどうかを考慮してTransferStartDateが返ってくるので過去日かどうかをクライアントは考慮しない
                minDate: symbolDetail.TransferStartDate ? new Date(symbolDetail.TransferStartDate) : null,
                maxDate: symbolDetail.TransferEndDate ? new Date(symbolDetail.TransferEndDate) : null,
                onChange: (value) => formik.setFieldValue('transferDate', value),
                onBlur: formik.handleBlur,
                isWeekEndDisable: true,
                title: '振込予定日',
                error: formik.touched.transferDate ? formik.errors.transferDate : '',
              },
              spinboxProps: {
                ...amountProps.spinboxProps,
              },
              showManualQuantity: amountProps.showManualQuantity,
            }}
          />
          <Caption label="確認事項" />
          <Description
            labelColor={labelColor.red}
            labelSize={labelSize.medium}
            label="すべての確認事項にチェックを入れると「申込内容の確認へ」がクリックできます。"
          />

          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※「米国不動産投資事業案件の契約締結前交付書面」のURLをクリックすることでチェックできるようになります。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※書面のダウンロードが必要な場合はブラウザに表示した後、ブラウザの機能で保存可能です。"
          />
          <CheckLabel
            checked={formik.values.isContractDocumentCheck}
            onChange={() => formik.setFieldValue('isContractDocumentCheck', !formik.values.isContractDocumentCheck)}
            link="「米国不動産投資事業案件の契約締結前交付書面」"
            href={symbolDetail.PreContractDocumentURL}
            after={
              <>
                をダウンロードし、その記載内容をよく読んで理解しました。
                <DescriptionAndLink
                  labelColor={labelColor.gray}
                  labelSize={labelSize.small}
                  before="※記載内容について、"
                  link={PagePathFactory.faqLinkSymbolProductType(ProductType.OverseasRealEstate)}
                  linkText="よくあるご質問と回答（FAQ）"
                  after="をまとめておりますのでご参照ください。さらにご質問がある場合は、画面上部の「お問い合わせ」からご連絡いただければ担当者よりお電話などにてご説明いたします。"
                />
                <br />
                <Description
                  labelColor={labelColor.gray}
                  labelSize={labelSize.small}
                  label="※記載内容を書面で交付することを希望される場合は「お問い合わせ」からお申し出ください。その場合、速やかにこの画面での契約締結を停止します。"
                />
              </>
            }
            linkClicked={formik.values.isContractDocumentOpened}
            onClick={() => {
              formik.setFieldValue('isContractDocumentOpened', true);
            }}
            linkClickedRequired={true}
            errorText="リンクをクリックして、「米国不動産投資事業案件の契約締結前交付書面」の内容を確認してください"
          />
          <CheckLabel
            checked={formik.values.isCheck2}
            onChange={() => formik.setFieldValue('isCheck2', !formik.values.isCheck2)}
            before="申込人の本出資にかかる情報は、出資契約の締結および本事業の遂行のため、組合契約地位譲渡人である株式会社ＦＰＧ証券に提供され、本事業が継続する間、貴社との間で相互に共有されることに同意します。"
          />
          <CheckLabel
            checked={formik.values.isCheck3}
            onChange={() => formik.setFieldValue('isCheck3', !formik.values.isCheck3)}
            before="事業損益予想表の数値は金融情勢および米国不動産市況等に基づき試算した予想数字です。収入、経費、修繕費、米国税率、売却価格、為替レート等の変動により事業収支が変動する等、将来にわたり組合損益、現金分配額等が確定したものではないことを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck4}
            onChange={() => formik.setFieldValue('isCheck4', !formik.values.isCheck4)}
            before="貴社所定の審査の結果によっては、本事業への出資ができないことがあることを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck5}
            onChange={() => formik.setFieldValue('isCheck5', !formik.values.isCheck5)}
            before="本事業への出資が申込人の意向に沿った内容となっていることを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck6}
            onChange={() => formik.setFieldValue('isCheck6', !formik.values.isCheck6)}
            before="価格変動リスク、流動性リスクおよび為替リスク等の元本毀損リスクその他上記の契約締結前交付書面に記載の投資リスクについてすべて理解しました。これらの投資リスクにより、場合によっては出資金（当初出資金の他追加出資金を含む）の全額または一部を回収できないことがあることを理解しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck7}
            onChange={() => formik.setFieldValue('isCheck7', !formik.values.isCheck7)}
            before="株式会社FPGより勧誘を受けるに際し、不確実な事実について、断定的判断の提供を受けたり、確実であると誤解させる恐れのあることを告げられたりした事実はないことを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck8}
            onChange={() => formik.setFieldValue('isCheck8', !formik.values.isCheck8)}
            before="出資する金融商品が預金等ではなく、預金保険法第五十三条に規定する保険金の支払い（いわゆる「預金保険機構」により保険金の支払い）の対象とならないことを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck9}
            onChange={() => formik.setFieldValue('isCheck9', !formik.values.isCheck9)}
            before={`お客さまの氏名その他の申告事項に変更がないことを確認しました。
              （変更がある場合は、右上の「登録情報＞ご登録内容の照会/変更」より、変更をお願いいたします）`}
          />
          <CheckLabel
            checked={formik.values.isCheck10}
            onChange={() => formik.setFieldValue('isCheck10', !formik.values.isCheck10)}
            before="本申込みの撤回ができないことを確認しました。"
          />
          {/* 紹介者情報を非表示 */}
          {/* <div className="programDetail_introductionConfirmation">
            <div>本お申込みにあたり、紹介者さまから、紹介を受けましたか？</div>
            <Selector
              selectedOption={formik.values.introductionConfirmation}
              setSelectedOption={(val) => formik.setFieldValue('introductionConfirmation', val)}
              options={IntroductionConfirmationStrs}
              error={formik.touched.introductionConfirmation ? formik.errors.introductionConfirmation : ''}
            />
          </div> */}
          <Button
            label="申込内容の確認へ"
            buttonFill={ButtonFill.black}
            buttonSize={ButtonSize.large}
            disable={!canApplySymbol}
            onClick={() => {
              showMessageModal(
                `お申込み金額は${formatPrice(
                  formik.values.amount!,
                )}円です。\n金額にお間違えのないようお手続きを進めてください。\n※お振込金額はお申込み金額に経過利息を加えた金額となります。\n\n適合性チェックがお済でないかたは、適合性チェックを行っていただいてから「申込内容の確認」にお進みいただきます。`,
                () => {
                  const id = crypto.randomUUID();
                  formik.setFieldValue('clientRequestId', id);
                  formik.handleSubmit();
                },
                '申込内容の確認へ',
              );
            }}
          />
          <MessageModal {...modalProps} shouldCloseOnOverlayClick={false} />
        </div>
      </div>
    </div>
  );
};
